import { Col, Input, Row, Spin, Skeleton, Statistic } from "antd";
import { useEffect, useState } from "react";

import {
    ShoppingOutlined, // For Total Orders
    DollarOutlined, // For Gross Sale
    BankOutlined, // For Net Sale
    FileTextOutlined, // For Total Taxes
    PercentageOutlined, // For Total Discount
    CarOutlined, // For Delivery Amounts
    WalletOutlined, // For Cash Sale
    CreditCardOutlined, // For Card Sale
} from "@ant-design/icons";

import { useDispatch, useSelector } from "react-redux";
import StatisticCard from "../../components/NewComponents/StatisticCard.jsx";
import {
    formatCurrencyValue,
    nFormatter,
} from "../../functions/generalFunctions";
import {
    changeDateFrom,
    changeDateTo,
    changeBranch,
} from "../../redux/actions/authAction";
import { FunnelPlotOutlined, LoadingOutlined } from "@ant-design/icons";
import { INPUT_SIZE } from "../../common/ThemeConstants";
import FormButton from "../../components/general/FormButton";
import FormSelect from "../../components/general/FormSelect";
import { getDate } from "../../functions/dateFunctions";
import { postRequest } from "../../services/mainApp.service";
import { COMPANY_ADMIN } from "../../common/SetupMasterEnum.js";
import TopProductsTable from "./TopProductsTable.jsx";
import DonutChart from "./DonutChart.jsx";
import CustomBarChart from "./CustomBarChart.jsx";
import AdvancedComprehensiveChart from "./AdvancedComprehensiveChart.jsx";
import OrdersDashboard from "./OrdersDashboard.jsx";

export const GraphicalDashboard = () => {
    const dispatch = useDispatch();
    const [salesData, setSalesData] = useState([])

    const [dashboardData, setDashboardData] = useState([]);
    const [reportData, setReportData] = useState({});
    const [paymentMode, setPaymentMode] = useState([]);
    const [productlist, setProductList] = useState([]);
    const [orderMode, setOrderMode] = useState([]);
    const [orderSource, setOrderSource] = useState([]);
    const [branchList, setBranchList] = useState([]);
    const [companySaleList, setCompanySaleList] = useState([]);
    const [branchDropdownList, setBranchDropdownList] = useState([]);
    const [orderData, setOrderData] = useState([]);
    const [lastThreeMonthData, setLastThreeMonthData] = useState()
    const [loading, setLoading] = useState(false);
    const appStore = useSelector((state) => state.authReducer);
    const isDashboard = appStore.menuList.some((item) => item.Menu_URL == "#");

    const processBranchData = (orderData) => {
        if (!orderData || !orderData.length) return [];

        const branchData = orderData.reduce((acc, order) => {
            const branchName = order.BranchName || 'Unknown Branch';

            if (!acc[branchName]) {
                acc[branchName] = {
                    BranchName: branchName,
                    OrderCount: 0,
                    TotalRevenue: 0,
                    Received_Amount: 0,
                };
            }

            acc[branchName].OrderCount += 1;
            acc[branchName].TotalRevenue += order.NetAmount || 0;
            acc[branchName].Received_Amount += order.Received_Amount || 0;

            return acc;
        }, {});

        return Object.values(branchData).sort((a, b) => b.OrderCount - a.OrderCount);
    };

    const processShiftData = (orderData) => {
        if (!orderData || !orderData.length) return [];

        const shiftData = orderData.reduce((acc, order) => {
            const shiftName = order.ShiftName || 'No Shift';
            const businessDate = order.BusinessDate ? new Date(order.BusinessDate) : new Date(order.OrderDateTime);
            const hour = businessDate.getHours();

            // Determine shift based on time if ShiftName is not available
            let actualShiftName = shiftName;
            if (shiftName === 'No Shift' || !shiftName) {
                if (hour >= 6 && hour < 14) {
                    actualShiftName = 'Morning Shift';
                } else if (hour >= 14 && hour < 22) {
                    actualShiftName = 'Evening Shift';
                } else {
                    actualShiftName = 'Night Shift';
                }
            }

            if (!acc[actualShiftName]) {
                acc[actualShiftName] = {
                    ShiftName: actualShiftName,
                    OrderCount: 0,
                    TotalRevenue: 0
                };
            }

            acc[actualShiftName].OrderCount += 1;
            acc[actualShiftName].TotalRevenue += order.NetAmount || 0;

            return acc;
        }, {});

        return Object.values(shiftData).sort((a, b) => b.OrderCount - a.OrderCount);
    };


    function convertToChartData(data) {
        const mapping = {
            DeliveryCharges: "Delivery Charges",
            Discount: "Discount",
            Gross: "Gross Sale",
            NetSale: "Net Sale",
            Tax: "Tax Amount"
        };

        const chartData = [];

        const total = Object.keys(mapping).reduce((sum, key) => {
            return sum + (data[key] || 0);
        }, 0);

        for (const key in mapping) {
            chartData.push({
                OrderCount: data[key] || 0,
                OrderStatus: mapping[key],
                Percentage: total ? Number(((data[key] / total) * 100).toFixed(2)) : 0
            });
        }

        return chartData;
    }

    const {
        companyList,
        RoleName,
        CompanyId,
        RoleId,
        dateFrom,
        dateTo,
        branchId,
    } = useSelector((state) => state.authReducer);

    const [data, setData] = useState({
        DateFrom: getDate(Date.now() - 30 * 24 * 60 * 60 * 1000),
        DateTo: getDate(),
        BranchId: null,
    });

    const { RefreshStats } = useSelector((state) => state.RefreshReducer);


    const chartsConfig = [
        {
            title: "Sale Details",
            data: salesData,
            dataKey: "OrderCount",
            link: "sale-details",
            nameKey: "OrderStatus",
            isOrderStatus: true,
            type: "donut",
            chartType: "sale-details"
        },
        {
            title: "Order By Status",
            data: dashboardData?.Table1,
            dataKey: "OrderCount",
            link: "order-by-status",
            nameKey: "OrderStatus",
            isOrderStatus: true,
            type: "donut",
            chartType: "order-status"
        },
        {
            title: "New vs Returning Customers",
            data: dashboardData?.Table6,
            dataKey: "OrderCount",
            link: "customers-type",
            nameKey: "CustomerType",
            type: "donut",
            chartType: "customer-types"
        },
        {
            title: "Payment Type",
            data: dashboardData?.Table8,
            dataKey: "OrderCount",
            nameKey: "PaymentMode",
            link: "payment-type",
            type: "donut",
            chartType: "payment-types"
        },

        {
            title: "Order By Source",
            data: dashboardData?.Table3,
            dataKey: "OrderCount",
            nameKey: "OrderSource",
            link: "order-by-source",
            type: "donut",
            chartType: "order-sources"
        },
        {
            title: "Sales By Source",
            data: dashboardData?.Table3,
            dataKey: "NetSales",
            nameKey: "OrderSource",
            link: "order-by-source",
            type: "bar",
            chartType: "order-sources"
        },

        {
            title: "Order By Mode",
            data: dashboardData?.Table11,
            dataKey: "OrderCount",
            nameKey: "OrderMode",
            link: "order-by-mode",
            type: "donut",
            chartType: "order-mode"
        },
        {
            title: "Sales By Mode",
            data: dashboardData?.Table11,
            dataKey: "NetSales",
            nameKey: "OrderMode",
            link: "sales-by-mode",
            type: "bar",
            chartType: "sales-mode"
        },
        {
            title: "Order By Depatment",
            data: dashboardData?.Table14,
            dataKey: "Quantity",
            nameKey: "DepartmentName",
            link: "order-by-department",
            type: "donut",
            chartType: "order-department"
        },
        {
            title: "Sales By Depatment",
            data: dashboardData?.Table14,
            dataKey: "SalesAmount",
            nameKey: "DepartmentName",
            link: "sales-by-department",
            type: "bar",
            chartType: "sales-department"
        },
        {
            title: "Order By Waiter",
            data: dashboardData?.Table12,
            dataKey: "OrderCount",
            nameKey: "WaiterName",
            link: "order-by-waiter",
            type: "donut",
            chartType: "order-waiter"
        },
        {
            title: "Sales By Waiter",
            data: dashboardData?.Table12,
            dataKey: "NetSales",
            nameKey: "WaiterName",
            link: "sales-by-waiter",
            type: "bar",
            chartType: "sales-waiter"
        },
        {
            title: "Order By Gender",
            data: dashboardData?.Table7,
            link: "order-by-gender",
            dataKey: "OrderCount",
            nameKey: "Title",
            type: "bar",
            chartType: "order-gender"
        },
        {
            title: "Order By Rider",
            data: dashboardData?.Table13,
            link: "order-by-rider",
            dataKey: "OrderCount",
            nameKey: "RiderName",
            type: "bar",
            chartType: "order-rider"
        },
        {
            title: "Order by Category",
            data: dashboardData?.Table9,
            dataKey: "Quantity",
            link: "order-by-category",
            nameKey: "CategoryName",
            type: "donut",
            chartType: "order-categories"
        },
        {
            title: "Sales by Category",
            data: dashboardData?.Table9,
            dataKey: "SalesAmount",
            link: "sales-by-category",
            nameKey: "CategoryName",
            type: "bar",
            chartType: "sales-categories"
        },
        // {
        //     title: "Sales by Area",
        //     data: dashboardData?.Table4,
        //     dataKey: "TotalSales",
        //     link: "sales-by-area",
        //     nameKey: "AreaName",
        //     type: "bar",
        //     chartType: "sales-areas"
        // },
        // NEW CHARTS FROM Dashboard_order API
        {
            title: "Order By Branch",
            data: processBranchData(orderData),
            dataKey: "OrderCount",
            link: "order-by-branch",
            nameKey: "BranchName",
            type: "donut",
            chartType: "order-branch"
        },
        {
            title: "Sales By Branch",
            data: processBranchData(orderData),
            dataKey: "TotalRevenue",
            link: "sales-by-branch",
            nameKey: "BranchName",
            type: "bar",
            chartType: "sales-branch"
        },
        {
            title: "Shift-Wise Orders",
            data: processShiftData(orderData),
            dataKey: "OrderCount",
            link: "shift-wise-orders",
            nameKey: "ShiftName",
            type: "donut",
            chartType: "shift-orders"
        },
        {
            title: "Shift-Wise Sales",
            data: processShiftData(orderData),
            dataKey: "TotalRevenue",
            link: "shift-wise-sales",
            nameKey: "ShiftName",
            type: "bar",
            chartType: "shift-sales"
        }
    ];


    const productsColumns = [

        {
            title: "Product",
            dataIndex: "Product",
            key: "Product",
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: "Times Sold",
            dataIndex: "Quantity",
            key: "Quantity",
            align: "right",
            render: (quantity) => <span>{quantity}</span>,
        },
        {
            title: "Sales Amount",
            dataIndex: "SalesAmount",
            key: "SalesAmount",
            align: "right",
            render: (amount) => <span>Rs. {amount.toLocaleString("en-PK")}</span>,
        },
    ];

    const categoriesColumns = [
        {
            title: "Category Name",
            dataIndex: "CategoryName",
            key: "CategoryName",
            render: (text) => <strong>{text}</strong>,
        },

        {
            title: "Sales Amount",
            dataIndex: "SalesAmount",
            key: "SalesAmount",
            align: "right",
            render: (amount) => <span>Rs. {amount.toLocaleString("en-PK")}</span>,
        },
        {
            title: "Percentage",
            dataIndex: "Percentage",
            key: "Percentage",
            align: "right",
            render: (percentage) => <span>{percentage.toFixed(2)}%</span>,
        },
    ];

    const customerColumns = [
        {
            title: "Customer Name",
            dataIndex: "CustomerName",
            key: "CustomerName",
            render: (text) => <strong>{text}</strong>,
        },
        {
            title: "Phone Number",
            dataIndex: "PhoneNumber",
            key: "PhoneNumber",
            render: (text) => <span>{text}</span>,
        },
        {
            title: "No of Orders",
            dataIndex: "OrderCount",
            key: "OrderCount",
            align: "right",
            render: (count) => (
                <span>{count}</span>
            ),
        },
        {
            title: "Net Sales",
            dataIndex: "NetSales",
            key: "NetSales",
            align: "right",
            render: (amount) => (
                <span>Rs. {amount.toLocaleString("en-PK")}</span>
            ),
        },
    ];


    useEffect(() => {
        if (RefreshStats == 1) {
            getDashboardData();
            getOrderData();
            getLastThreeMonthData()
            dispatch({ type: "REFRESH_STATS", payload: 0 });
        }
    }, [RefreshStats]);

    useEffect(() => {
        getDashboardData();
        getOrderData();
        getLastThreeMonthData()

    }, []);

    const getDashboardData = () => {
        postRequest("PosDashboard", {
            OperationId: 1, CompanyId: appStore.CompanyId,
            BranchId:
                appStore.userBranchList?.length > 0 &&
                    appStore.IsPos === true &&
                    appStore.RoleId !== COMPANY_ADMIN
                    ? appStore.userBranchList[0]?.BranchId
                    : RoleId === 14
                        ? branchId
                        : data.BranchId,
            DateFrom: RoleId === 14 ? dateFrom : data.DateFrom,
            DateTo: RoleId === 14 ? dateTo : data.DateTo,
        }).then((res) => {
            setLoading(false);
            setDashboardData(res?.data?.DataSet);
            const sale = convertToChartData(res.data.DataSet.Table[0])
            setSalesData(sale)
            setReportData(res.data.DataSet.Table[0]);
            setOrderMode(res.data.DataSet.Table1);
            setOrderSource(res.data.DataSet.Table2);
            setPaymentMode(res.data.DataSet.Table3);
            setProductList(res.data.DataSet.Table4);
            setBranchList(res.data.DataSet.Table5);
            setCompanySaleList(res.data.DataSet.Table6);
            setBranchDropdownList(res.data.DataSet.Table7);
        }).catch((err) => {
            console.log(err)
        })
    };
    const getLastThreeMonthData = () => {
        setLoading(true);

        const dateToValue = RoleId === 14 ? dateTo : data.DateTo;
        const dateFromThreeMonthsOldValue = new Date(new Date(dateToValue) - 30 * 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
        console.log(dateToValue)
        console.log(dateFromThreeMonthsOldValue)
        postRequest("Dashboard_Order", {
            OperationId: 1,
            CompanyId: appStore.CompanyId,
            BranchId:
                appStore.userBranchList?.length > 0 &&
                    appStore.IsPos === true &&
                    appStore.RoleId !== COMPANY_ADMIN
                    ? appStore.userBranchList[0]?.BranchId
                    : RoleId === 14
                        ? branchId
                        : data.BranchId,
            DateFrom: dateFromThreeMonthsOldValue || new Date(Date.now() - 30 * 3 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            DateTo: dateToValue || new Date().toISOString().split('T')[0]
        }).then((res) => {
            setLoading(false);
            console.log("API Response:", res);
            const orders = res?.data?.Data || [];
            setLastThreeMonthData(orders)
        }).catch((err) => {
            setLoading(false);
            console.log("API Error:", err);
        });
    };

    const getOrderData = () => {
        setLoading(true);
        const dateFromValue = RoleId === 14 ? dateFrom : data.DateFrom;
        const dateToValue = RoleId === 14 ? dateTo : data.DateTo;

        postRequest("Dashboard_Order", {
            OperationId: 1,
            CompanyId: appStore.CompanyId,
            BranchId:
                appStore.userBranchList?.length > 0 &&
                    appStore.IsPos === true &&
                    appStore.RoleId !== COMPANY_ADMIN
                    ? appStore.userBranchList[0]?.BranchId
                    : RoleId === 14
                        ? branchId
                        : data.BranchId,
            DateFrom: dateFromValue || new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
            DateTo: dateToValue || new Date().toISOString().split('T')[0]
        }).then((res) => {
            setLoading(false);
            console.log("API Response:", res);
            const orders = res?.data?.Data || [];
            setOrderData(orders); // Store the order data for charts
        }).catch((err) => {
            setLoading(false);
            console.log("API Error:", err);
        });
    };


    return (
        <div style={{ display: "flex", height: "100%", width: "100%" }}>
            <div style={{ display: "flex", flexDirection: "column", width: "100%" }}>


                {isDashboard && (
                    <>
                        <div className="mt-5">
                            {/* <h2>Dashboardd</h2> */}
                            <div className="flex flex-wrap gap-4 items-end p-4 bg-gray-50 rounded-lg border border-gray-200">
                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date From
                                    </label>
                                    <Input
                                        type="date"
                                        className="w-full h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500 rounded-md"
                                        value={RoleId === 14 ? dateFrom : data.DateFrom}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeDateFrom(e.target.value))
                                                : setData({ ...data, DateFrom: e.target.value });
                                        }}
                                    />
                                </div>

                                <div className="flex-1 min-w-[200px]">
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Date To
                                    </label>
                                    <Input
                                        type="date"
                                        className="w-full h-10 border-gray-300 hover:border-blue-400 focus:border-blue-500 rounded-md"
                                        value={RoleId === 14 ? dateTo : data.DateTo}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeDateTo(e.target.value))
                                                : setData({ ...data, DateTo: e.target.value });
                                        }}
                                    />
                                </div>

                                {RoleName === "Management" && RoleId === 14 && (
                                    <div className="flex-1 min-w-[200px]">
                                        <FormSelect
                                            value={CompanyId}
                                            onChange={(e) => {
                                                handleCompanyChange(e.value);
                                            }}
                                            label="Select Company"
                                            listItem={companyList}
                                            idName="CompanyId"
                                            valueName="CompanyName"
                                            placeholder="Choose a Company"
                                            className="w-full"
                                        />
                                    </div>
                                )}

                                <div className="flex-1 min-w-[200px]">
                                    <FormSelect
                                        listItem={
                                            RoleId === 14
                                                ? branchDropdownList.filter(
                                                    (company) => company.CompanyId === CompanyId
                                                )
                                                : appStore.userBranchList || []
                                        }
                                        idName="BranchId"
                                        valueName="BranchName"
                                        size={INPUT_SIZE}
                                        className="w-full"
                                        label="Select Branch"
                                        value={RoleId === 14 ? branchId : data.BranchId}
                                        onChange={(e) => {
                                            RoleId === 14
                                                ? dispatch(changeBranch(e.value))
                                                : setData({ ...data, BranchId: e.value });
                                        }}
                                    />
                                </div>

                                <div className="flex items-end h-10">
                                    <FormButton
                                        title={RoleId === 14 ? "" : "Apply"}
                                        icon={<FunnelPlotOutlined />}
                                        sm={6}
                                        type="primary"
                                        onClick={() => {
                                            dispatch({ type: "REFRESH_STATS", payload: 1 });

                                        }}
                                        className="h-10 bg-blue-600 hover:bg-blue-700 border-0 rounded-md shadow-sm min-w-[60px]"
                                    />
                                </div>
                            </div>
                            <div className="mt-7 grid grid-cols-1 gap-3 md:sgrid-cols-2 lg:grid-cols-3 xl:grid-cols-4 w-full">
                                <>
                                    <StatisticCard
                                        title="Total Orders"
                                        value={
                                            RoleId === 14
                                                ? formatCurrencyValue(reportData?.TotalOrders_Landed)
                                                : nFormatter(reportData?.TotalOrders_Landed, 1)
                                        }
                                        statIcon={ShoppingOutlined}
                                        loading={loading}
                                        className="bg-blue-600 p-3 rounded-full text-white "
                                    />
                                </>
                                <>
                                    <StatisticCard
                                        // title={`Gross Sale ${
                                        //   RoleId === 14 ? "(PKR)" : "(Rs)"
                                        // } Without Tax`}
                                        title="Gross Sale"
                                        value={
                                            // RoleId === 14
                                            //   ? formatCurrencyValue(reportData?.Gross)
                                            //   : nFormatter(parseFloat(reportData?.Gross), 1)
                                            parseFloat(reportData?.Gross) || 0
                                        }
                                        statIcon={DollarOutlined}
                                        className="bg-blue-600 p-3 rounded-full text-white "
                                        loading={loading}
                                    />
                                </>

                                <>
                                    <StatisticCard
                                        // title={`Net Sale ${
                                        //   RoleId === 14 ? "(PKR)" : "(Rs)"
                                        // } Without Tax`}
                                        title="Net Sale"
                                        value={
                                            // RoleId === 14
                                            //   ? formatCurrencyValue(reportData?.NetSale)
                                            //   : nFormatter(parseFloat(reportData?.NetSale), 1)
                                            parseFloat(reportData?.NetSale) || 0
                                        }
                                        statIcon={BankOutlined}
                                        className="bg-yellow-500 p-3 rounded-full text-white"
                                        loading={loading}
                                    />
                                </>

                                <>
                                    <StatisticCard
                                        title={`Total Taxes ${RoleId === 14 ? "(PKR)" : "(Rs)"}`}
                                        value={
                                            // RoleId === 14
                                            //   ? formatCurrencyValue(reportData?.Tax)
                                            //   : nFormatter(parseFloat(reportData?.Tax), 1)
                                            parseFloat(reportData?.Tax) || 0
                                        }
                                        statIcon={FileTextOutlined}
                                        className="bg-red-500 p-3 rounded-full text-white "
                                        loading={loading}
                                    />
                                </>

                                <>
                                    <div>
                                        <StatisticCard
                                            title={`Total Discount ${RoleId === 14 ? "(PKR)" : "(Rs)"
                                                }`}
                                            value={
                                                // RoleId === 14
                                                //   ? formatCurrencyValue(reportData?.Discount)
                                                //   : nFormatter(parseFloat(reportData?.Discount), 1)
                                                parseFloat(reportData?.Discount) || 0
                                            }
                                            statIcon={PercentageOutlined}
                                            className="bg-purple-500 p-3 rounded-full text-white "
                                            loading={loading}
                                        />
                                    </div>
                                </>
                                <div>
                                    <StatisticCard
                                        title={`Delivery Charges ${RoleId === 14 ? "(PKR)" : "(Rs)"
                                            }`}
                                        value={
                                            // RoleId === 14
                                            //   ? formatCurrencyValue(reportData?.DeliveryCharges)
                                            //   : nFormatter(
                                            //       parseFloat(reportData?.DeliveryCharges),
                                            //       1
                                            //     )
                                            parseFloat(reportData?.DeliveryCharges) || 0
                                        }
                                        statIcon={CarOutlined}
                                        className="bg-orange-500 p-3 rounded-full text-white "
                                        loading={loading}
                                    />
                                </div>
                                <>
                                    <div>
                                        <StatisticCard
                                            title={`Cash Sale ${RoleId === 14 ? "(PKR)" : "(Rs)"}`}
                                            value={
                                                // RoleId === 14
                                                //   ? formatCurrencyValue(reportData?.Cash)
                                                //   : nFormatter(parseFloat(reportData?.Cash), 1)
                                                // parseFloat(reportData?.Cash)
                                                0
                                            }
                                            statIcon={WalletOutlined}
                                            className="bg-teal-500 p-3 rounded-full text-white "
                                            loading={loading}
                                        />
                                    </div>
                                </>
                                <>
                                    <div>
                                        <StatisticCard
                                            title={`Card Sale ${RoleId === 14 ? "(PKR)" : "(Rs)"}`}
                                            value={
                                                // RoleId === 14
                                                //   ? formatCurrencyValue(reportData?.Cash)
                                                //   : nFormatter(parseFloat(reportData?.Cash), 1)
                                                // parseFloat(reportData?.Card)
                                                0
                                            }
                                            statIcon={CreditCardOutlined}
                                            className="bg-teal-500 p-3 rounded-full text-white "
                                            loading={loading}
                                        />
                                    </div>
                                </>
                            </div>
                        </div>
                    </>
                )}

                <AdvancedComprehensiveChart
                    dashboardData={dashboardData}
                    orderData={orderData}
                    loading={loading}
                    dateFrom={RoleId === 14 ? dateFrom : data.DateFrom}
                    dateTo={RoleId === 14 ? dateTo : data.DateTo}
                />
                <OrdersDashboard
                    data={lastThreeMonthData}
                    title="Last Three Months Data"
                    showTopCount={5}
                    showFullTable={false}
                />
                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-2 gap-2">
                    {chartsConfig.map((chart, index) => (
                        <div key={index}>
                            {chart.type === 'donut' ? (
                                <DonutChart
                                    data={chart.data}
                                    loading={loading}
                                    dataKey={chart.dataKey}
                                    nameKey={chart.nameKey}
                                    isOrderStatus={chart.isOrderStatus}
                                    title={chart.title}
                                    chartType={chart.chartType}
                                    link={chart.link}
                                />
                            ) : chart.type === 'bar' ? (
                                <CustomBarChart
                                    data={chart.data}
                                    loading={loading}
                                    dataKey={chart.dataKey}
                                    nameKey={chart.nameKey}
                                    link={chart.link}
                                    title={chart.title}
                                    chartType={chart.chartType}
                                />
                            ) : null}
                        </div>
                    ))}
                </div>

                <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-2 xl:grid-cols-3 gap-2">
                    {/* Your existing tables */}
                    <TopProductsTable
                        data={dashboardData?.Table2}
                        columns={productsColumns}
                        title="Most Sold Products"
                        showTopCount={10}
                        loading={loading}
                        link="top-products"
                    />

                    <TopProductsTable
                        data={dashboardData?.Table9}
                        columns={categoriesColumns}
                        title="Sales by Category"
                        showTopCount={10}
                        loading={loading}
                        link="category-sales"
                    />

                    <TopProductsTable
                        data={dashboardData?.Table10}
                        columns={customerColumns}
                        title="Top Customers"
                        showTopCount={10}
                        loading={loading}
                        link="top-customers"
                    />
                </div>
            </div>
        </div>
    )
}
